import React, { useMemo } from 'react';
import { Box, Paper, styled } from '@mui/material';
import { extractInspectionDateFromExecutiveSummary } from '../utils/notesTemplates.util';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  margin: theme.spacing(2),
  minHeight: '842px', // A4 height
  width: '595px', // A4 width
  backgroundColor: '#fff',
  fontSize: '12px',
  lineHeight: 1.4,
  '*': {
    fontFamily: 'Times New Roman, serif',
  },
  '& p': {
    margin: '0.3em 0',
  },
  // Ensure blank lines created in the editor are visible in preview
  '& p:empty::before, & p:has(br)::before': {
    content: '" "',
    whiteSpace: 'pre',
  },
  // Logo styling
  '& img.company-logo, & img[alt="CD Construction Consulting"]': {
    width: '150px !important',
    height: 'auto !important',
    display: 'block !important',
    margin: '0 auto 2rem auto !important',
    maxWidth: '150px !important',
  },
  // Override signature image styles in preview mode
  '& img[alt="Signature"]': {
    // The preview page is A4 in points (595x842), so 72px = 1in: keep the signature at least 1in tall.
    height: '80px !important',
    width: 'auto !important',
    maxWidth: '260px !important',
    maxHeight: '80px !important',
  },
}));

interface CoverPagePreviewProps {
  content: string;
  executiveSummary?: string;
}

const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const CoverPagePreview: React.FC<CoverPagePreviewProps> = ({ content, executiveSummary }) => {
  // Extract site visit from executive summary
  const siteVisit = useMemo(() => {
    if (!executiveSummary) return '';
    return extractInspectionDateFromExecutiveSummary(executiveSummary);
  }, [executiveSummary]);

  // Inject site visit into the cover page content
  const enhancedContent = useMemo(() => {
    if (!siteVisit || !content) return content;

    // Find the Project # line and add Site Visit Number after it
    const projectNumberPattern = /(<p><strong>Project #<\/strong>[^<]*<\/p>)/i;

    if (projectNumberPattern.test(content)) {
      return content.replace(projectNumberPattern, `$1\n        <p><strong>Site Visit Date:</strong> ${escapeHtml(siteVisit)}</p>`);
    }

    return content;
  }, [content, siteVisit]);

  return (
    <StyledPaper elevation={2}>
      {/* eslint-disable-next-line react/no-danger */}
      <Box dangerouslySetInnerHTML={{ __html: enhancedContent }} />
    </StyledPaper>
  );
};

export default CoverPagePreview;
